import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, CalendarDays, ArrowUpRight } from "lucide-react";
import { useLanguage, useSEO } from "../context/LanguageContext";
import { PageHero, Reveal, Overline } from "../components/Kinetic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventCard = ({ e, t, lang, past = false }) => {
  const locale = lang === "fr" ? "fr-FR" : "en-GB";
  const d = new Date(e.date);
  const dateLabel = d.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const timeLabel = e.date && e.date.length > 10 ? d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) : null;
  const title = lang === "en" && e.title_en ? e.title_en : e.title_fr;
  const description = lang === "en" && e.description_en ? e.description_en : e.description_fr;

  return (
    <article
      className={`border p-8 transition-colors duration-300 ${past ? "border-graphite/10 bg-white/60" : "border-graphite/15 bg-white hover:border-forest/40"}`}
      data-testid={`event-card-${e.id}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-block bg-forest-soft px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-forest">
          {t(`events.typeLabels.${e.type}`)}
        </span>
        {past && (
          <span className="inline-block border border-graphite/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-soft">
            {t("events.past")}
          </span>
        )}
      </div>
      <h3 className="mt-5 font-display text-2xl md:text-3xl font-light tracking-tight text-graphite">{title}</h3>
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-graphite-soft">
        <span className="inline-flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-forest" />
          {dateLabel}
          {timeLabel ? ` — ${timeLabel}` : ""}
        </span>
        {e.location && (
          <span className="inline-flex items-center gap-2">
            <MapPin className="h-4 w-4 text-forest" />
            {e.location}
          </span>
        )}
      </div>
      {description && <p className="mt-4 max-w-2xl whitespace-pre-wrap text-sm md:text-base leading-relaxed text-graphite-soft">{description}</p>}
      {e.link && (
        <a
          href={e.link}
          target="_blank"
          rel="noreferrer"
          className="group mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-forest hover:text-graphite transition-colors"
          data-testid={`event-link-${e.id}`}
        >
          {t("events.moreInfo")}
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      )}
    </article>
  );
};

const EvenementsPage = () => {
  const { t, lang } = useLanguage();
  useSEO(t("events.metaTitle"), t("events.metaDesc"));
  const [events, setEvents] = useState(null);

  useEffect(() => {
    axios.get(`${API}/events`).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (events || []).filter((e) => (e.date || "") >= today);
  const past = (events || []).filter((e) => (e.date || "") < today).reverse();
  const isEmpty = events !== null && upcoming.length === 0 && past.length === 0;

  return (
    <div data-testid="evenements-page">
      <PageHero
        overline={t("events.overline")}
        titleLines={t("events.titleLines")}
        intro={t("events.intro")}
        testid="evenements"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-5xl px-6">
          {isEmpty ? (
            <>
              <Reveal>
                <div className="border border-graphite/15 bg-white p-10 md:p-16" data-testid="events-empty-state">
                  <Overline>{t("status.prep")}</Overline>
                  <h2 className="mt-5 max-w-2xl font-display text-3xl md:text-4xl font-light tracking-tight leading-tight text-graphite">
                    {t("events.emptyTitle")}
                  </h2>
                  <p className="mt-5 max-w-2xl text-base leading-relaxed text-graphite-soft">{t("events.emptyText")}</p>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="overline-mono mt-16 text-forest">{t("events.typesTitle")}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  {t("events.types").map((type) => (
                    <span key={type} className="inline-block border border-graphite/15 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-graphite-soft">
                      {type}
                    </span>
                  ))}
                </div>
              </Reveal>
            </>
          ) : (
            <div className="space-y-16">
              {upcoming.length > 0 && (
                <div>
                  <Reveal y={16}>
                    <Overline>{t("events.upcoming")}</Overline>
                  </Reveal>
                  <div className="mt-8 space-y-5" data-testid="events-upcoming-list">
                    {upcoming.map((e, i) => (
                      <Reveal key={e.id} delay={i * 0.06}>
                        <EventCard e={e} t={t} lang={lang} />
                      </Reveal>
                    ))}
                  </div>
                </div>
              )}
              {past.length > 0 && (
                <div>
                  <Reveal y={16}>
                    <Overline className="text-graphite-soft">{t("events.past")}</Overline>
                  </Reveal>
                  <div className="mt-8 space-y-5" data-testid="events-past-list">
                    {past.map((e, i) => (
                      <Reveal key={e.id} delay={i * 0.06}>
                        <EventCard e={e} t={t} lang={lang} past />
                      </Reveal>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EvenementsPage;
